import { useEffect, useRef } from 'react';
import { Mic, MicOff, AlertCircle } from 'lucide-react';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import type { SpeechInputButtonProps } from '../types/speech';

export function SpeechInputButton({
  onTranscript,
  disabled = false,
  className = '',
  appendMode = true,
}: SpeechInputButtonProps) {
  const transcriptRef = useRef('');

  const {
    isListening,
    isSupported,
    error,
    startListening,
    stopListening,
    resetTranscript,
  } = useSpeechRecognition({
    language: 'de-DE',
    continuous: true,
    interimResults: true,
    onResult: (text, isFinal) => {
      if (isFinal) {
        transcriptRef.current += text;
        onTranscript(transcriptRef.current.trim());
      }
    },
    onEnd: () => {
      if (transcriptRef.current) {
        onTranscript(transcriptRef.current.trim());
      }
    },
  });

  const handleClick = () => {
    if (disabled || !isSupported) return;

    if (isListening) {
      stopListening();
    } else {
      if (!appendMode) {
        transcriptRef.current = '';
        resetTranscript();
      }
      startListening();
    }
  };

  // Reset transcript ref when starting fresh
  useEffect(() => {
    if (!isListening && !appendMode) {
      transcriptRef.current = '';
    }
  }, [isListening, appendMode]);

  if (!isSupported) {
    return (
      <button
        type="button"
        disabled
        className={`p-2 rounded-xl bg-secondary-100 text-secondary-400 cursor-not-allowed ${className}`}
        title="Spracherkennung wird nicht unterstützt"
      >
        <MicOff size={20} />
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        className={`
          relative p-2 rounded-xl transition-all duration-200
          ${
            isListening
              ? 'bg-red-500 text-white shadow-lg shadow-red-200 scale-110'
              : disabled
              ? 'bg-secondary-100 text-secondary-400 cursor-not-allowed'
              : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200 hover:text-secondary-800'
          }
          ${className}
        `}
        title={isListening ? 'Aufnahme stoppen' : 'Spracheingabe starten'}
      >
        {isListening ? <Mic size={20} className="animate-pulse" /> : <Mic size={20} />}

        {/* Pulsing ring animation when listening */}
        {isListening && (
          <>
            <span className="absolute inset-0 rounded-xl animate-ping bg-red-400 opacity-30" />
            <span className="absolute -inset-1 rounded-xl animate-pulse bg-red-300 opacity-20" />
          </>
        )}
      </button>

      {/* Error tooltip */}
      {error && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600 whitespace-nowrap shadow-lg z-10">
          <div className="flex items-center gap-1">
            <AlertCircle size={12} />
            <span>{error.message}</span>
          </div>
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 w-2 h-2 bg-red-50 border-r border-b border-red-200 transform rotate-45" />
        </div>
      )}
    </div>
  );
}

export default SpeechInputButton;
